import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, ChevronUp, ChevronDown, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import {
  CustomFieldDefinition,
  CustomFieldType,
  fetchCustomFieldDefinitions,
  notifyCustomFieldsChanged,
} from '../../lib/crm/customFields';

interface CrmCustomFieldsModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
}

type EditorType = 'text' | 'number' | 'dropdown';

interface EditorState {
  id: string | null; // null = creating a new field
  name: string;
  type: EditorType;
  required: boolean;
  choices: string[];
  allowCustom: boolean;
  min: number;
  max: number;
}

const emptyEditor = (): EditorState => ({
  id: null,
  name: '',
  type: 'text',
  required: false,
  choices: [],
  allowCustom: true,
  min: 0,
  max: 100,
});

const TYPE_LABELS: Record<string, string> = {
  text: 'Text',
  number: 'Number',
  dropdown: 'Dropdown',
  textarea: 'Text area',
  date: 'Date',
};

export default function CrmCustomFieldsModal({ isOpen, onClose, businessId }: CrmCustomFieldsModalProps) {
  const [fields, setFields] = useState<CustomFieldDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [newChoice, setNewChoice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setFields(await fetchCustomFieldDefinitions(businessId));
    } catch (err) {
      console.error('Error loading custom fields:', err);
      toast.error('Failed to load custom fields');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    if (isOpen) {
      setEditor(null);
      setNewChoice('');
      void load();
    }
  }, [isOpen, load]);

  const openEditor = (field?: CustomFieldDefinition) => {
    setNewChoice('');
    if (!field) {
      setEditor(emptyEditor());
      return;
    }
    const type: EditorType = field.type === 'number' || field.type === 'dropdown' ? field.type : 'text';
    setEditor({
      id: field.id,
      name: field.name,
      type,
      required: field.required,
      choices: field.options?.choices || [],
      allowCustom: field.options?.allowCustom !== false,
      min: field.options?.min ?? 0,
      max: field.options?.max ?? 100,
    });
  };

  const buildOptions = (e: EditorState) => {
    if (e.type === 'dropdown') return { choices: e.choices, allowCustom: e.allowCustom };
    if (e.type === 'number') return { min: e.min, max: e.max };
    return null;
  };

  const saveEditor = async () => {
    if (!editor) return;
    const name = editor.name.trim();
    if (!name) {
      toast.error('Enter a field name');
      return;
    }
    if (editor.type === 'dropdown' && editor.choices.length === 0 && !editor.allowCustom) {
      toast.error('Add at least one choice or allow custom values');
      return;
    }
    if (editor.type === 'number' && editor.max < editor.min) {
      toast.error('Max must be greater than min');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        field_name: name,
        field_type: editor.type,
        is_required: editor.required,
        options: buildOptions(editor),
        updated_at: new Date().toISOString(),
      };
      if (editor.id) {
        const { error } = await supabase
          .from('opportunity_custom_fields')
          .update(payload)
          .eq('id', editor.id)
          .eq('business_id', businessId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('opportunity_custom_fields')
          .insert({
            business_id: businessId,
            ...payload,
            display_order: fields.length,
          });
        if (error) throw error;
      }
      setEditor(null);
      await load();
      notifyCustomFieldsChanged();
    } catch (err: any) {
      console.error('Error saving custom field:', err);
      toast.error(err?.message?.includes('duplicate') ? 'A field with this name already exists' : 'Failed to save field');
    } finally {
      setSaving(false);
    }
  };

  const deleteField = async (field: CustomFieldDefinition) => {
    if (!window.confirm(`Delete the "${field.name}" field? Existing leads keep their saved values, but the field will no longer appear on new leads.`)) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('opportunity_custom_fields')
        .delete()
        .eq('id', field.id)
        .eq('business_id', businessId);
      if (error) throw error;
      if (editor?.id === field.id) setEditor(null);
      await load();
      notifyCustomFieldsChanged();
    } catch (err) {
      console.error('Error deleting custom field:', err);
      toast.error('Failed to delete field');
    } finally {
      setSaving(false);
    }
  };

  const moveField = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= fields.length) return;
    const a = fields[index];
    const b = fields[target];
    // Optimistic swap
    const next = [...fields];
    next[index] = b;
    next[target] = a;
    setFields(next);
    try {
      const updates = next.map((f, i) =>
        supabase.from('opportunity_custom_fields').update({ display_order: i }).eq('id', f.id).eq('business_id', businessId)
      );
      const results = await Promise.all(updates);
      const failed = results.find(r => r.error);
      if (failed?.error) throw failed.error;
      notifyCustomFieldsChanged();
    } catch (err) {
      console.error('Error reordering custom fields:', err);
      toast.error('Failed to reorder fields');
      void load();
    }
  };

  const addChoice = () => {
    if (!editor) return;
    const value = newChoice.trim();
    if (!value) return;
    if (editor.choices.some(c => c.toLowerCase() === value.toLowerCase())) {
      toast.error('That choice already exists');
      return;
    }
    setEditor({ ...editor, choices: [...editor.choices, value] });
    setNewChoice('');
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100200] flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative h-full w-full max-w-md bg-gray-50 shadow-2xl flex flex-col animate-[slideInRight_0.2s_ease-out]">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-gray-900">Custom Fields</h2>
            <p className="text-xs text-gray-500 mt-0.5">Extra fields shown when creating a lead</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Field list */}
          {loading ? (
            <p className="text-sm text-gray-400 text-center py-6">Loading…</p>
          ) : fields.length === 0 && !editor ? (
            <p className="text-sm text-gray-400 text-center py-6">No custom fields yet</p>
          ) : (
            <div className="space-y-2">
              {fields.map((field, index) => (
                <div key={field.id} className="bg-white rounded-xl border border-gray-200 px-3 py-2.5 flex items-center gap-2">
                  <div className="flex flex-col">
                    <button
                      onClick={() => moveField(index, -1)}
                      disabled={index === 0 || saving}
                      className="p-0.5 text-gray-300 hover:text-gray-600 disabled:opacity-30 transition-colors"
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => moveField(index, 1)}
                      disabled={index === fields.length - 1 || saving}
                      className="p-0.5 text-gray-300 hover:text-gray-600 disabled:opacity-30 transition-colors"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">
                      {field.name}
                      {field.required && <span className="text-red-500 ml-0.5">*</span>}
                    </div>
                    <div className="text-xs text-gray-500">
                      {TYPE_LABELS[field.type] || field.type}
                      {field.type === 'dropdown' && field.options?.choices?.length
                        ? ` · ${field.options.choices.length} choice${field.options.choices.length === 1 ? '' : 's'}${field.options.allowCustom !== false ? ' + custom' : ''}`
                        : ''}
                      {field.type === 'number' ? ` · ${field.options?.min ?? 0}–${field.options?.max ?? 100}` : ''}
                    </div>
                  </div>
                  <button
                    onClick={() => openEditor(field)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors touch-manipulation"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => deleteField(field)}
                    disabled={saving}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors touch-manipulation"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Editor */}
          {editor ? (
            <div className="bg-white rounded-xl border border-blue-200 p-4 space-y-4">
              <h3 className="text-sm font-bold text-gray-900">{editor.id ? 'Edit field' : 'New field'}</h3>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Field name</label>
                <input
                  type="text"
                  value={editor.name}
                  onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                  placeholder="e.g. Vehicle Type"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Field type</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['text', 'number', 'dropdown'] as EditorType[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setEditor({ ...editor, type: t })}
                      className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors touch-manipulation ${
                        editor.type === t
                          ? 'bg-blue-50 border-blue-400 text-blue-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {TYPE_LABELS[t]}
                    </button>
                  ))}
                </div>
              </div>

              {editor.type === 'dropdown' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Choices</label>
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="text"
                        value={newChoice}
                        onChange={(e) => setNewChoice(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addChoice(); } }}
                        placeholder="Add a choice…"
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                      />
                      <button
                        type="button"
                        onClick={addChoice}
                        disabled={!newChoice.trim()}
                        className={`p-2 rounded-lg transition-colors touch-manipulation ${
                          newChoice.trim() ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    {editor.choices.length === 0 ? (
                      <p className="text-xs text-gray-400">No choices yet</p>
                    ) : (
                      <div className="space-y-1">
                        {editor.choices.map((choice) => (
                          <div key={choice} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded-lg">
                            <span className="text-sm text-gray-700">{choice}</span>
                            <button
                              type="button"
                              onClick={() => setEditor({ ...editor, choices: editor.choices.filter(c => c !== choice) })}
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors touch-manipulation"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <div className="text-sm font-medium text-gray-800">Allow custom option</div>
                      <p className="text-xs text-gray-500">Adds a "Custom…" choice that lets you type your own value</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={editor.allowCustom}
                      onChange={(e) => setEditor({ ...editor, allowCustom: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </label>
                </div>
              )}

              {editor.type === 'number' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Pick-list range</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={editor.min}
                      onChange={(e) => setEditor({ ...editor, min: Number(e.target.value) || 0 })}
                      className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                    />
                    <span className="text-sm text-gray-500">to</span>
                    <input
                      type="number"
                      value={editor.max}
                      onChange={(e) => setEditor({ ...editor, max: Number(e.target.value) || 0 })}
                      className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5">Shown as a dropdown of these numbers — typing a number directly also works.</p>
                </div>
              )}

              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <div className="text-sm font-medium text-gray-800">Required</div>
                  <p className="text-xs text-gray-500">Must be filled when creating a lead in Nexus. Webhooks and automations are never blocked.</p>
                </div>
                <input
                  type="checkbox"
                  checked={editor.required}
                  onChange={(e) => setEditor({ ...editor, required: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </label>

              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={saveEditor}
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.99] transition disabled:opacity-50 touch-manipulation"
                >
                  {saving ? 'Saving…' : editor.id ? 'Save changes' : 'Add field'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditor(null)}
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition touch-manipulation"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => openEditor()}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-blue-600 bg-blue-50 border border-blue-100 hover:bg-blue-100 active:scale-[0.99] transition touch-manipulation"
            >
              <Plus className="h-4 w-4" />
              Add custom field
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
